<template>
  <dp-seite titel="Planungskalender" untertitel="Gespeicherter Entwurf und vorgemerkte Änderungen – bearbeitet wird in der Musterzuordnung" :snack="snack">
    <template #aktionen>
      <v-btn small depressed color="primary" @click="$router.push('/dienstplanung/zuordnung')"><v-icon small left>mdi-plus</v-icon>Neue Zuordnung</v-btn>
    </template>
    <template #leiste>
      <v-tabs v-model="tab" height="40" class="px-3" style="flex: 0 0 auto; border-bottom: 1px solid #e0e0e0">
        <v-tab>Kolonnen &amp; Teams</v-tab>
        <v-tab>Mitarbeiter</v-tab>
        <v-tab>Maschinen</v-tab>
      </v-tabs>
    </template>
    <template v-if="dp.plan">
      <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
        <zeitraum-wahl :value="dp.range" @input="r => reloadPlan(r)"/>
        <v-spacer/>
        <v-text-field v-if="tab === 1" v-model="suche" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify" label="Mitarbeiter suchen" style="max-width: 260px"/>
      </div>

      <div class="kal-scroll">
        <table class="kal">
          <thead>
          <tr>
            <th class="sticky">{{ ['Kolonne / Team', 'Mitarbeiter', 'Maschine'][tab] }}</th>
            <th v-for="w in wochen" :key="w.key" :class="{hol: feiertag(w)}">
              KW {{ w.nummer }}<small>{{ fmtDayShort(w.montag) }}–{{ fmtDayShort(w.sonntag) }}</small>
              <small v-if="tab === 2">{{ parityText(w.paritaet) }}</small>
              <small v-if="feiertag(w)" class="red--text" :title="feiertag(w)">Feiertag</small>
            </th>
          </tr>
          </thead>
          <tbody>
          <!-- crews and machines: pattern-week mappings -->
          <template v-if="tab !== 1">
            <tr v-for="g in zeilenGruppen" :key="g.id">
              <td class="sticky"><b>{{ g.bezeichnung }}</b><small>{{ g.nummer }}{{ g.typ === 'TEAM' ? ' · Team' : '' }}</small></td>
              <td v-for="w in wochen" :key="w.key">
                <button class="cell" :class="mapCls(g, w)" @click="oeffne(g, w)">
                  {{ mapText(g, w) }}<small>{{ mapSub(g, w) }}</small>
                </button>
              </td>
            </tr>
            <tr v-if="!zeilenGruppen.length"><td :colspan="wochen.length + 1" class="grey--text pa-4">Keine {{ tab === 0 ? 'Kolonnen oder Teams' : 'Maschinen' }} angelegt (Planungsgruppen).</td></tr>
          </template>
          <!-- employees: weekly sources -->
          <template v-else>
            <tr v-for="m in zeilenMitarbeiter" :key="m.bis_id">
              <td class="sticky"><b>{{ name(m.bis_id) }}</b><small>{{ m.pers_number }}</small></td>
              <td v-for="w in wochen" :key="w.key">
                <button class="cell" :class="quelleCls(m, w)" @click="$router.push({path: '/dienstplanung/besetzung', query: {woche: w.key}})">
                  {{ quelleKurz(m, w) }}<small>{{ quelleSub(m, w) }}</small>
                </button>
              </td>
            </tr>
            <tr v-if="!zeilenMitarbeiter.length"><td :colspan="wochen.length + 1" class="grey--text pa-4">Keine Mitarbeiter mit Wochenquelle oder Pool im Zeitraum.</td></tr>
          </template>
          </tbody>
        </table>
      </div>
      <div class="legend mt-2">
        <span><i class="ok"></i>gespeichert</span><span><i class="staged"></i>vorgemerkt</span><span><i class="warn"></i>Raster seit Zuordnung geändert / Lücke</span><span><i class="none"></i>nicht zugeordnet</span>
        <span class="ml-3">Jede Zelle verweist auf Raster + Phase + Woche (mit gespeicherten Tageswerten). Klick öffnet die Zuordnung.</span>
      </div>
    </template>
  </dp-seite>
</template>

<script>
import {fmtDayShort, fmtHHMM, parityText, periodWeeks, QUELLEN, weekHours} from '@/shared/dienstplan_api'
import seite from './seite'
import {dp, effBesetzung, effZuordnung, gruppeName, isStagedB, isStagedZ, kolonnen, maschinen, name, position, zuordnungLabel} from './store'
import DpSeite from './components/DpSeite'
import ZeitraumWahl from './components/ZeitraumWahl'

export default {
  name: 'DpKalender',
  mixins: [seite],
  components: {DpSeite, ZeitraumWahl},
  data: () => ({tab: 0, suche: ''}),
  computed: {
    wochen () {
      return periodWeeks(dp.range.von, dp.range.bis)
    },
    zeilenGruppen () {
      return this.tab === 0 ? kolonnen() : maschinen()
    },
    zeilenMitarbeiter () {
      const ids = new Set()
      dp.plan.besetzungen.forEach(b => ids.add(b.mita_bis_id))
      Object.values(dp.staged.besetzungen).forEach(b => ids.add(b.mita_bis_id))
      kolonnen().concat(maschinen()).forEach(g => g.mitglieder.forEach(m => ids.add(m.mita_bis_id)))
      const s = (this.suche || '').toLowerCase()
      return dp.basis.mitarbeiter.filter(m => ids.has(m.bis_id) && (!s || name(m.bis_id).toLowerCase().includes(s) || m.pers_number.includes(s)))
        .sort((a, b) => name(a.bis_id).localeCompare(name(b.bis_id)))
    },
  },
  created () {
    this.ensure()
    if (this.$route.query.tab) this.tab = Number(this.$route.query.tab) || 0
  },
  methods: {
    name,
    fmtDayShort,
    parityText,
    ziel () {
      return this.tab === 2 ? 'MACHINE' : 'CREW'
    },
    feiertag (w) {
      return Object.entries(dp.feiertage).filter(([d]) => d >= w.montag && d <= w.sonntag).map(([, n]) => n).join(', ')
    },
    mapText (g, w) {
      const z = effZuordnung(this.ziel(), g.id, w.key)
      return z ? zuordnungLabel(z) : 'Zuordnen'
    },
    mapSub (g, w) {
      const z = effZuordnung(this.ziel(), g.id, w.key)
      if (isStagedZ(this.ziel(), g.id, w.key)) return z ? 'vorgemerkt' : 'Entfernung vorgemerkt'
      if (!z) return ''
      return z.raster_geaendert ? 'Raster geändert' : fmtHHMM(weekHours(z.tage)) + ' Soll'
    },
    mapCls (g, w) {
      const z = effZuordnung(this.ziel(), g.id, w.key)
      if (isStagedZ(this.ziel(), g.id, w.key)) return 'staged'
      return !z ? 'none' : z.raster_geaendert ? 'warn' : 'ok'
    },
    oeffne (g, w) {
      this.$router.push({path: '/dienstplanung/zuordnung', query: {typ: this.ziel(), ziel: g.id, woche: w.key, schritt: 3}})
    },
    quelleKurz (m, w) {
      const b = effBesetzung(m.bis_id, w.key)
      if (!b) return '–'
      if (b.typ === 'CREW') return gruppeName(b.planungsgruppe_id)
      if (b.typ === 'MACHINE') return (position(b.fahrerposition_id) || {}).bezeichnung || 'Fahrer'
      if (b.typ === 'DIRECT') return zuordnungLabel(effZuordnung('DIRECT', m.bis_id, w.key))
      return QUELLEN[b.typ]
    },
    quelleSub (m, w) {
      const b = effBesetzung(m.bis_id, w.key)
      if (isStagedB(m.bis_id, w.key)) return 'vorgemerkt'
      return b ? QUELLEN[b.typ] : (this.hatQuelle(m) ? 'Lücke' : 'Standard')
    },
    quelleCls (m, w) {
      if (isStagedB(m.bis_id, w.key)) return 'staged'
      const b = effBesetzung(m.bis_id, w.key)
      if (b) return 'ok'
      return this.hatQuelle(m) ? 'warn' : 'none'
    },
    hatQuelle (m) {
      return this.wochen.some(w => effBesetzung(m.bis_id, w.key))
    },
  },
}
</script>

<style scoped>
.kal-scroll {
  overflow: auto;
  border: 1px solid #d1d7dc;
  max-height: calc(100vh - 260px);
}
.kal {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}
.kal th,
.kal td {
  border-bottom: 1px solid #e3e6e9;
  border-right: 1px solid #f0f0f0;
  padding: 6px;
  text-align: center;
  vertical-align: middle;
}
.kal thead th {
  position: sticky;
  top: 0;
  background: #f4f5f6;
  font-weight: 500;
  z-index: 2;
  white-space: nowrap;
}
.kal th small,
.kal td small {
  display: block;
  font-size: 10px;
  color: #78909c;
  font-weight: 400;
}
.kal .sticky {
  position: sticky;
  left: 0;
  background: #fff;
  text-align: left;
  min-width: 190px;
  z-index: 1;
  border-right: 2px solid #e0e0e0;
}
.kal thead .sticky {
  z-index: 3;
  background: #f4f5f6;
}
.kal th.hol {
  background: #ffebee;
}
.cell {
  display: block;
  width: 100%;
  min-width: 112px;
  text-align: left;
  padding: 6px 7px;
  border: 1px solid #d4daf0;
  background: #f1f3fc;
  color: #303f9f;
  border-radius: 2px;
  font-size: 11px;
}
.cell:hover {
  background: #e3e7fb;
}
.cell.none {
  border: 1px dashed #cfd8dc;
  background: #fafafa;
  color: #90a4ae;
}
.cell.staged {
  border-color: #81c784;
  background: #e8f5e9;
  color: #1b5e20;
}
.cell.warn {
  border-color: #ffb74d;
  background: #fff8e1;
  color: #e65100;
}
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 11px;
  color: #78909c;
  align-items: center;
}
.legend i {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 4px;
  border: 1px solid #d4daf0;
  background: #f1f3fc;
}
.legend i.staged {
  background: #e8f5e9;
  border-color: #81c784;
}
.legend i.warn {
  background: #fff8e1;
  border-color: #ffb74d;
}
.legend i.none {
  background: #fafafa;
  border-style: dashed;
  border-color: #cfd8dc;
}
</style>
